import { supabase } from "@/integrations/supabase/client";

export interface ResumeMatchTest {
  id: string;
  title: string;
  content: string;
  fileName: string;
  fileType: string;
  dateCreated: string;
  matchScore: number;
  jobTitle: string;
  companyName: string;
  analysisResults: any;
}

export class ResumeMatchTestService {
  /**
   * Get all resume match tests for the current user
   */
  static async getResumeMatchTests(): Promise<ResumeMatchTest[]> {
    const { data, error } = await supabase
      .from('resume_match_tests')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching resume match tests:', error);
      throw new Error('Failed to fetch resume match tests');
    }

    return data.map(test => ({
      id: test.id,
      title: test.title,
      content: test.content,
      fileName: test.file_name || '',
      fileType: test.file_type || '',
      dateCreated: test.created_at,
      matchScore: test.match_score || 0,
      jobTitle: test.job_title || '',
      companyName: test.company_name || '',
      analysisResults: test.analysis_results
    }));
  }

  /**
   * Save a resume match test result
   */
  static async saveResumeMatchTest(
    title: string, 
    content: string, 
    matchScore: number,
    jobTitle: string,
    companyName: string,
    analysisResults: any,
    fileName?: string, 
    fileType?: string
  ): Promise<ResumeMatchTest> {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      throw new Error('User must be authenticated to save resume match tests');
    }

    const { data, error } = await supabase
      .from('resume_match_tests')
      .insert({
        user_id: session.user.id,
        title,
        content,
        file_name: fileName || null,
        file_type: fileType || null,
        match_score: matchScore,
        job_title: jobTitle,
        company_name: companyName,
        analysis_results: analysisResults
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving resume match test:', error);
      throw new Error('Failed to save resume match test');
    }

    return {
      id: data.id,
      title: data.title,
      content: data.content,
      fileName: data.file_name || '',
      fileType: data.file_type || '',
      dateCreated: data.created_at,
      matchScore: data.match_score || 0,
      jobTitle: data.job_title || '',
      companyName: data.company_name || '',
      analysisResults: data.analysis_results
    };
  }

  /**
   * Delete a resume match test
   */
  static async deleteResumeMatchTest(testId: string): Promise<void> {
    const { error } = await supabase
      .from('resume_match_tests')
      .delete()
      .eq('id', testId);

    if (error) {
      console.error('Error deleting resume match test:', error);
      throw new Error('Failed to delete resume match test');
    }
  }
}